/*
MIT License

Copyright (c) 2018 Shopify, 2023 VRIFY Technologies Inc.

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import * as draggable from '@shopify/draggable'
import { makeMultiNodeNode } from '@/lib/editor/multi-node-node'

const onContextMenuWhileDragging = Symbol('onContextMenuWhileDragging')
const onMouseDown = Symbol('onMouseDown')
const onMouseMove = Symbol('onMouseMove')
const onMouseUp = Symbol('onMouseUp')
const startDrag = Symbol('startDrag')
const onDistanceChange = Symbol('onDistanceChange')

/**
 * Get the closest parent element of a given element that matches the given
 * selector string or matching function
 */
function closest(element: Element, value: string | ((el: Element) => Element) | Element | Element[] | NodeList) {
  if (!element) {
    return null
  }

  function conditionFn(currentElement: Element) {
    if (!currentElement) {
      return currentElement
    } else if (typeof value === 'string') {
      return Element.prototype.matches.call(currentElement, value)
    } else if (value instanceof NodeList || value instanceof Array) {
      return Array.from(value).includes(currentElement)
    } else if (value instanceof HTMLElement) {
      return value === currentElement
    } else if (typeof value === 'function') {
      return value(currentElement)
    } else {
      return null
    }
  }

  let current: Element | null = element

  do {
    if (conditionFn(current)) {
      return current
    }

    current = current.parentNode as Element
  } while (current && current !== document.body && (current as Node) !== document)

  return null
}

/**
 * Returns the distance between two points
 */
function euclideanDistance(x1: number, y1: number, x2: number, y2: number) {
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
}

/**
 * This sensor picks up native browser mouse events and dictates drag operations
 */
// @ts-ignore
export class MultiDragSensor extends draggable.Sensors.Sensor {
  private _mouseDownTimeout?: number
  private _selectedElements: Element[] = []
  private pageX: number | null = null
  private pageY: number | null = null

  constructor(
    containers: HTMLElement[],
    options: { delay?: number; handle?: Element; excludeHandle?: string; distance?: number; draggable: Element }
  ) {
    super(containers, options)

    this.options = options
    this.containers = containers
    document.addEventListener('mousedown', this[onMouseDown], true)
  }

  // @ts-ignore temporary - outdated @shopify/draggable types
  containers: HTMLElement[]
  // @ts-ignore temporary - outdated @shopify/draggable types
  options: {
    delay?: number
    handle?: Element
    excludeHandle?: string
    distance?: number
    draggable: Element
  }
  // @ts-ignore temporary - outdated @shopify/draggable types
  delay: { mouse: any }
  // @ts-ignore temporary - outdated @shopify/draggable types
  onMouseDownAt?: number
  // @ts-ignore temporary - outdated @shopify/draggable types
  startEvent: MouseEvent | null
  // @ts-ignore temporary - outdated @shopify/draggable types
  currentContainer: Element | null
  // @ts-ignore temporary - outdated @shopify/draggable types
  originalSource: HTMLElement | null
  // @ts-ignore temporary - outdated @shopify/draggable types
  dragging: boolean

  attach() {
    document.addEventListener('mousedown', this[onMouseDown], true)
    return this
  }

  detach() {
    document.removeEventListener('mousedown', this[onMouseDown], true)
    return this
  }

  setSelection(elements: Element[]) {
    this._selectedElements = elements
  }

  [onMouseDown] = (event: MouseEvent) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey) {
      return
    }
    const target = event.target as Element
    const container = closest(target, this.containers)

    if (!container) {
      return
    }

    if (this.options.handle && event.target && !closest(target, this.options.handle)) {
      return
    }

    if (this.options.excludeHandle && event.target && closest(target, this.options.excludeHandle)) {
      return
    }

    const originalSource = closest(target, this.options.draggable)

    if (!originalSource) {
      return
    }

    const { pageX, pageY } = event

    Object.assign(this, { pageX, pageY })
    this.onMouseDownAt = Date.now()
    this.startEvent = event

    this.currentContainer = container
    document.addEventListener('mouseup', this[onMouseUp])
    document.addEventListener('dragstart', preventNativeDragStart)
    document.addEventListener('mousemove', this[onDistanceChange])

    this._mouseDownTimeout = window.setTimeout(() => {
      this[onDistanceChange]({ pageX: this.pageX!, pageY: this.pageY! })
    }, this.options.delay)
  };

  [startDrag] = () => {
    const startEvent = this.startEvent!
    const container = this.currentContainer
    const targetElement = closest(startEvent.target as Element, this.options.draggable)
    const dragNonSelected = !this._selectedElements.some(el => el === targetElement)

    let originalSource = makeMultiNodeNode(this._selectedElements, startEvent)

    if (dragNonSelected) {
      originalSource = makeMultiNodeNode([targetElement!], startEvent)
    }

    // @ts-ignore
    const dragStartEvent = new draggable.Sensors.DragStartSensorEvent({
      clientX: startEvent.clientX,
      clientY: startEvent.clientY,
      target: startEvent.target,
      container,
      originalSource,
      originalEvent: startEvent,
    })

    // @ts-ignore
    this.trigger(this.currentContainer as HTMLElement, dragStartEvent)

    this.dragging = !dragStartEvent.canceled()

    if (this.dragging) {
      document.addEventListener('contextmenu', this[onContextMenuWhileDragging], true)
      document.addEventListener('mousemove', this[onMouseMove])
    }
  };

  /**
   * Detect change in distance, starting drag when both
   * delay and distance requirements are met
   */
  [onDistanceChange] = (event: { pageX: number; pageY: number }) => {
    const { pageX, pageY } = event
    const { distance } = this.options
    const { startEvent, delay } = this

    Object.assign(this, { pageX, pageY })

    if (!this.currentContainer) {
      return
    }

    const timeElapsed = Date.now() - this.onMouseDownAt!
    const distanceTravelled = euclideanDistance(startEvent!.pageX, startEvent!.pageY, pageX, pageY) || 0

    clearTimeout(this._mouseDownTimeout)

    if (timeElapsed < (this.options.delay ?? 0)) {
      // moved during delay
      document.removeEventListener('mousemove', this[onDistanceChange])
    } else if (distanceTravelled >= distance!) {
      document.removeEventListener('mousemove', this[onDistanceChange])
      this[startDrag]()
    }
  };

  [onMouseMove] = (event: MouseEvent) => {
    if (!this.dragging) {
      return
    }

    const target = document.elementFromPoint(event.clientX, event.clientY)

    // @ts-ignore
    const dragMoveEvent = new draggable.Sensors.DragMoveSensorEvent({
      clientX: event.clientX,
      clientY: event.clientY,
      target,
      container: this.currentContainer,
      originalEvent: event,
    })

    // @ts-ignore
    this.trigger(this.currentContainer as HTMLElement, dragMoveEvent)
  };

  [onMouseUp] = (event: MouseEvent) => {
    clearTimeout(this._mouseDownTimeout)

    if (event.button !== 0) {
      return
    }

    document.removeEventListener('mouseup', this[onMouseUp])
    document.removeEventListener('dragstart', preventNativeDragStart)
    document.removeEventListener('mousemove', this[onDistanceChange])

    if (!this.dragging) {
      return
    }

    const target = document.elementFromPoint(event.clientX, event.clientY)

    // @ts-ignore
    const dragStopEvent = new draggable.Sensors.DragStopSensorEvent({
      clientX: event.clientX,
      clientY: event.clientY,
      target,
      container: this.currentContainer,
      originalEvent: event,
    })

    // @ts-ignore
    this.trigger(this.currentContainer as HTMLElement, dragStopEvent)

    document.removeEventListener('contextmenu', this[onContextMenuWhileDragging], true)
    document.removeEventListener('mousemove', this[onMouseMove])

    this.currentContainer = null
    this.dragging = false
    this.startEvent = null
  };

  [onContextMenuWhileDragging] = (event: MouseEvent) => {
    event.preventDefault()
  }
}

function preventNativeDragStart(event: Event) {
  event.preventDefault()
}
